// Copyright (c) Docugami, Inc. All rights reserved.

import { createFolderIdString, getDirectoryName, getDirectoryPath, getFileName, getParentDirectoryPath, isAncestor, isChild, isDirectory, UnassignedDocSetId } from "components/file_management/file_helpers";
import { DocumentStatus, FileInfo, FileManagerDataApi, IngestionIssues, IngestionStatus, IngestionWarnings } from "networking/api";
import { Event, IEvent } from "utils/Event";
import { Logger, LoggerCategory } from "utils/Logger";
import { store } from "store";
import { documentsApis } from "store/frontend/documentsEndpoints";

export interface DirectoryProperties {
    id: string,
    name: string,
    displayName?: string,
    path: string,
    parentPath?: string,
    parentId?: string,
    isHidden: boolean
}

export enum DirectoryStatus {
    Uninitialized,
    Initializing,
    Initialized
}

export class DgDirectory {
    private _properties: DirectoryProperties;
    private _status: DirectoryStatus = DirectoryStatus.Uninitialized;
    private readonly _onStatusChangedEvent: Event<[DirectoryStatus, DirectoryStatus]> = new Event();

    constructor(id: string, path: string, parentId?: string) {
        const parentPath = getParentDirectoryPath(path);
        const name = getDirectoryName(path);
        this._properties = {
            id: id,
            name: name,
            path: path,
            parentPath: !!parentPath ? parentPath : undefined,
            parentId: parentId,
            isHidden: isNameHidden(name)
        };
    }

    get properties(): DirectoryProperties { return this._properties; }
    get onStatusChanged(): IEvent<[DirectoryStatus, DirectoryStatus]> { return this._onStatusChangedEvent; }
    get status(): DirectoryStatus { return this._status; }
    set status(value: DirectoryStatus) {
        if (value !== this._status) {
            const oldValue = this._status;
            this._status = value;

            this._onStatusChangedEvent.raise([oldValue, value]);
        }
    }
}

function isNameHidden(name: string): boolean {
    return name.charAt(0) === '.';
}

export interface FileProperties {
    id: string,
    name: string,
    displayName?: string,
    path: string,
    parentPath?: string,
    parentId?: string,
    fileId: string;
    fileSizeKb: number;
    status: DocumentStatus;
    ingestionIssues?: IngestionIssues;
    ingestionStatus?: IngestionStatus;
    ingestionWarnings?: IngestionWarnings;
    createdAt: Date;
    // TODO: Below this line should not belong on a file
    clusterId: string;
    folderId: string;
    hasCpml: boolean;
    isHidden: boolean;
    isAssignmentPending?: boolean;
}

export type FileItem = DgFile | DgDirectory;

export class DgFile {
    private _onPropertyChangedEvent: Event<DgFile> = new Event();
    private _properties: FileProperties;

    constructor(info: FileInfo) {
        this._properties = DgFile.createProperties(info);
    }

    get properties(): FileProperties { return this._properties; }
    get onPropertyChangedEvent(): IEvent<DgFile> { return this._onPropertyChangedEvent; }

    updateProperties(properties: Partial<FileProperties>) {
        this._properties = {
            ...this._properties,
            ...properties
        };

        this._properties.folderId = `${this._properties.clusterId}:${this._properties.fileId}`;
        this._onPropertyChangedEvent.raise(this);
    }

    public static createProperties(info: FileInfo): FileProperties {
        if (!info.name) {
            throw new Error("Cannot create a file without a valid name.");
        }

        let name;
        let parentPath;
        if (!!info.displayName) {
            parentPath = getDirectoryPath(info.displayName);
            name = getFileName(info.displayName);
        } else {
            parentPath = getDirectoryPath(info.name);
            name = getFileName(info.name);
        }

        const clusterId = info.clusterId ?? UnassignedDocSetId;
        const fileId = info.id ?? '';
        return {
            id: fileId,
            name: name,
            displayName: info.displayName,
            path: info.name,
            parentPath: !!parentPath ? parentPath : undefined,
            parentId: info.parentDirectoryId,
            fileId: fileId,
            fileSizeKb: info.sizeKiloBytes,
            status: info.status,
            clusterId: clusterId,
            folderId: createFolderIdString(clusterId, fileId),
            ingestionIssues: info.ingestionIssues,
            ingestionStatus: info.ingestionStatus,
            ingestionWarnings: info.ingestionWarnings,
            createdAt: new Date(info.createdAt!),
            hasCpml: info.hasCpml,
            isHidden: isNameHidden(name)
        };
    }
}

export const ROOT_DIRECTORY_ID = ""

export class FileManager {
    private readonly _itemLookupById: Map<string, DgDirectory | DgFile> = new Map();
    private readonly _pendingIds: Set<string> = new Set();

    private readonly _onItemAdded: Event<DgFile | DgDirectory> = new Event();
    private readonly _onItemRemoved: Event<DgFile | DgDirectory> = new Event();
    private readonly _onFileUpdated: Event<[FileProperties, FileProperties]> = new Event();

    constructor() {
        this.getOrCreateDirectory(ROOT_DIRECTORY_ID, '');
    }

    get onItemAdded(): IEvent<DgFile | DgDirectory> { return this._onItemAdded };
    get onItemRemoved(): IEvent<DgFile | DgDirectory> { return this._onItemRemoved };
    get onFileUpdated(): IEvent<[FileProperties, FileProperties]> { return this._onFileUpdated };
    get items(): Array<DgFile | DgDirectory> { return Array.from(this._itemLookupById.values()) }


    async fetchFilesByIds(ids: string[], force: boolean = false): Promise<Array<DgFile | DgDirectory> | null> {
        if (ids.length === 0) {
            return [];
        }

        const result: Set<DgFile | DgDirectory> = new Set();

        const filteredIds: string[] = [];
        const directories: DgDirectory[] = [];
        for (const id of ids) {
            const item = this._itemLookupById.get(id);
            if (item instanceof DgDirectory) {
                // For now, filter out any directory that we've already initialized or are initializing since we expect delta
                // changes to come over SignalR. This logic can be improved later to refresh stale state or allow the user to
                // force a refresh of the view.
                if (!force && item.status !== DirectoryStatus.Uninitialized) {
                    // We already have this item
                    result.add(item);
                    continue;
                }

                directories.push(item);
            }
            else if ((item instanceof DgFile) && !force) {
                // Already have this item
                result.add(item);
                continue;
            }
            else if (this._pendingIds.has(id)) {
                continue;
            }

            filteredIds.push(id);
        }

        // Request any remaining IDs over the network
        if (filteredIds.length !== 0) {
            try {
                // Set each directory to initializing
                filteredIds.forEach(id => this._pendingIds.add(id));
                directories.forEach(dir => dir.status = DirectoryStatus.Initializing);

                // Fetch the file info data from the server
                const request = store.dispatch(documentsApis.endpoints.getFilesByIds.initiate(filteredIds));
                const fileInfos = await request.unwrap();
                request.unsubscribe();

                for (const info of fileInfos) {
                    if (!info.id) {
                        continue;
                    }

                    const item = await this.getOrCreateItemFromInfo(info);
                    result.add(item);
                }

                // These directories are now initialized
                directories.forEach(dir => dir.status = DirectoryStatus.Initialized);
                this._pendingIds.clear();
            }
            catch (error) {
                directories.forEach(dir => dir.status = DirectoryStatus.Uninitialized);
                this._pendingIds.clear();
                return null;
            }
        }

        return Array.from(result);
    }

    async assignFiles(docSetId: string, paths: string[]): Promise<void> {
        const revert = await this.updateFilesTransaction(paths, { clusterId: docSetId, hasCpml: false, isAssignmentPending: true });

        const resetPendingAssignment = () => {
            this.updateFilesTransaction(paths, { isAssignmentPending: undefined });
        }
        const resetToken = setTimeout(resetPendingAssignment, 60000);

        try {
            const fileManagerDataApi = new FileManagerDataApi();
            await fileManagerDataApi.fileManagerDataAssignFiles(docSetId, paths);
        }
        catch (error) {
            clearTimeout(resetToken);
            revert();
            throw error;
        }
    }

    async unassignFiles(paths: string[]): Promise<void> {
        const revert = await this.updateFilesTransaction(paths, { clusterId: UnassignedDocSetId, hasCpml: undefined });

        try {
            const fileManagerDataApi = new FileManagerDataApi();
            await fileManagerDataApi.fileManagerDataUnassignFiles(paths);
        }
        catch (error) {
            revert();
            throw error;
        }
    }

    async fetchErrorFiles(): Promise<DgFile[] | null> {
        try {
            const fileManagerDataApi = new FileManagerDataApi();
            const response = await fileManagerDataApi.fileManagerDataGetErrorFiles();
            const result: DgFile[] = [];
            const fileInfos: FileInfo[] = response.data;

            for (const info of fileInfos) {
                if (!info.name) {
                    continue;
                }

                const item = await this.getOrCreateItemFromInfo(info) as DgFile;
                result.push(item);
            }

            return result;
        }
        catch (error) {
            Logger.LogError(LoggerCategory.FileManager, "Failed to fetch error files.", error);
        }

        return null;
    }

    getDirectoryById(id: string): DgDirectory | null {
        const result = this._itemLookupById.get(id);
        if (result instanceof DgDirectory) {
            return result;
        }

        return null;
    }

    async isFileId(id: string): Promise<boolean> {
        const fileManagerDataApi = new FileManagerDataApi();
        const result = await fileManagerDataApi.fileManagerDataIsFileId(id);
        return result.data;
    }

    getFileById(id: string): DgFile | null {
        const result = this._itemLookupById.get(id);
        if (result instanceof DgFile) {
            return result;
        }

        return null;
    }

    getFileFromFolderId(id: string): DgFile | null {
        for (const item of Array.from(this._itemLookupById.values())) {
            if (item instanceof DgFile && item.properties.folderId === id) {
                return item;
            }
        }

        return null;
    }
    async getFilesById(id: string, recursive: boolean): Promise<DgFile[]> {
        return (await this.getItemsById(id, recursive)).filter(x => x instanceof DgFile) as DgFile[];
    }

    async getItemsById(id: string, recursive: boolean): Promise<Array<DgFile | DgDirectory>> {
        if (await isDirectory(id)) {
            const directory = this.getDirectoryById(id);
            return this.items.filter(item => recursive ?
                isAncestor(directory?.properties!.path!, item.properties) :
                isChild(directory?.properties!.path!, item.properties));
        }
        else {
            const file = this.getFileById(id);
            return !!file ? [file] : [];
        }
    }

    async delete(ids: string[]): Promise<boolean> {
        // TODO: We should support batch delete operations
        let success = true;
        const fileManagerApi = new FileManagerDataApi();
        for (const id of ids) {
            const item = this.removeItemById(id);
            try {
                await fileManagerApi.fileManagerDataDelete(id);
            }
            catch (error) {
                if (!!item) {
                    this.addItem(item);
                }
            }
        }

        return success;
    }

    public getOrCreateDirectory(id: string, path: string, parentId?: string): DgDirectory {
        const directoryPath = getDirectoryPath(path);

        // Find directory by Id
        let directory = this._itemLookupById.get(id) as DgDirectory;

        if (!directory) {
            directory = new DgDirectory(id, directoryPath, parentId);
            this.addItem(directory);
        }

        return directory;
    }

    public getOrCreateDirectoryById(id: string, path: string, parentId?: string): DgDirectory {
        let directory = this._itemLookupById.get(id) as DgDirectory;
        if (!directory) {
            directory = new DgDirectory(id, path, parentId);
            this.addItem(directory);
        }

        return directory;
    }

    private async getOrCreateFile(info: FileInfo): Promise<DgFile> {
        if (await isDirectory(info.id!)) {
            throw new Error("Cannot create a file for a directory path.");
        }

        // Try to find existing file by ID
        let file = info.id ? this._itemLookupById.get(info.id) as DgFile : undefined;

        if (!file) {
            file = new DgFile(info);
            this.addItem(file);

            // Ensure the file's parent directory exists
            if (file.properties.parentId && file.properties.parentPath) {
                this.getOrCreateDirectory(file.properties.parentId, file.properties.parentPath);
            }

            // Ensure root directory exists
            this.getOrCreateDirectory(ROOT_DIRECTORY_ID, '');
        }
        else {
            this.updateFile(file, DgFile.createProperties(info));
        }

        return file;
    }

    /**
     * Creates or updates an item from FileInfo using ID-based lookup.
     */
    private async getOrCreateItemFromInfo(info: FileInfo): Promise<DgFile | DgDirectory> {
        let item: DgFile | DgDirectory;
        if (await isDirectory(info.id!)) {
            item = this.getOrCreateDirectoryById(info.id!, info.name!, info.parentDirectoryId);
        }
        else {
            item = await this.getOrCreateFile(info);
        }

        return item;
    }

    private removeItemById(id: string): DgFile | DgDirectory | undefined {
        const item = this._itemLookupById.get(id);
        if (!!item) {
            return this.removeItem(item);
        }

        return undefined;
    }

    private removeItem(item: DgFile | DgDirectory): DgFile | DgDirectory | undefined {
        const deleted = this._itemLookupById.delete(item.properties.path);
        if (deleted) {
            this._onItemRemoved.raise(item)
        }

        return deleted ? item : undefined;
    }

    private async addItem(item: DgFile | DgDirectory): Promise<void> {
        this._itemLookupById.set(item.properties.id, item);
        this._onItemAdded.raise(item);
    }

    private async updateFilesTransaction(ids: string[], props: Partial<FileProperties>): Promise<() => void> {
        const fileArrays = Promise.all(ids.map (id => this.getFilesById(id, true)));
        const allFiles = new Set<DgFile>((await fileArrays).flat())
        const filesToDocSet = Array.from(allFiles).map(x => {
            return {
                previousProps: { ...x.properties },
                file: x
            }
        });

        filesToDocSet.forEach(x => {
            this.updateFile(x.file, props);
        });

        return () => {
            filesToDocSet.forEach(x => {
                this.updateFile(x.file, x.previousProps);
            });
        }
    }

    private updateFile(file: DgFile, props: Partial<FileProperties>) {
        const oldProps = { ...file.properties };
        file.updateProperties(props);
        this._onFileUpdated.raise([oldProps, file.properties]);
    }
}

export const FileManagerInstance = new FileManager();
